import { useState, useMemo, useEffect } from "react";
import { v4 as uuidV4 } from "uuid";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/components/ui/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCreateAchTemplateInstance,
  useListAchTemplates,
  useGetAchPreviewTemplateDocument,
  CreateTemplateInstanceValidation,
  type ListAchTemplateItem,
  PreviewBodyValidation,
} from "@/lib/api/ach";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, FileText } from "lucide-react";

export interface AchFormData {
  ach_document_id: string;
  template_id: string;
  customer_name: string;
  customer_email: string;
  customer_phone_number?: string;
  lender_email?: string;
  lender_name?: string;
  lender_institution?: string;
  lender_phone_number?: string;
  lender_title?: string;
  document_title?: string;
  is_test_send: boolean;
  has_lienholder: boolean;
  ach_form_country_type: "USA" | "CAD";
}

interface SendAchFormCoreProps {
  initialData?: Partial<AchFormData>;
  taskInstanceId?: string;
  allowEditLienholder?: boolean;
  allowEditCountryType?: boolean;
  onSuccess?: () => void;
  onCancel?: () => void;
  resetTrigger?: boolean; // Trigger to reset form (for modal close)
  includeFreightTemplate?: boolean; // Trigger to reset form (for modal close)
}

const defaultFormData: AchFormData = {
  ach_document_id: "",
  template_id: "",
  customer_name: "",
  customer_email: "",
  customer_phone_number: "",
  lender_email: "",
  lender_name: "",
  lender_institution: "",
  lender_phone_number: "",
  lender_title: "",
  document_title: "",
  is_test_send: false,
  has_lienholder: false,
  ach_form_country_type: "USA",
};

export function SendAchFormCore({
  initialData,
  taskInstanceId,
  allowEditLienholder = true,
  allowEditCountryType = true,
  onSuccess,
  onCancel,
  resetTrigger,
  includeFreightTemplate = false,
}: SendAchFormCoreProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const createAchTemplateInstance = useCreateAchTemplateInstance();
  const getAchPreview = useGetAchPreviewTemplateDocument();
  const templatesResult = useListAchTemplates();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    lender_email?: string;
    lender_institution?: string;
    lender_name?: string;
  }>({});

  // Get templates list
  const templates = useMemo(() => {
    return templatesResult?.data?.data?.data || [];
  }, [templatesResult]);

  // Filter out templates with "freight" or "vendor" in the name
  const filteredTemplates = useMemo(() => {
    return templates.filter((template: ListAchTemplateItem) => {
      const templateTitle = (template.DocumentTemplate?.title || "").toLowerCase();
      if (includeFreightTemplate) { return true; }
      return !templateTitle.includes("freight") && !templateTitle.includes("vendor");
    });
  }, [templates]);

  // Initialize form data from initialData or defaults
  const [formData, setFormData] = useState<AchFormData>(() => {
    if (initialData) {
      return {
        ...defaultFormData,
        ...initialData,
        customer_phone_number: initialData.customer_phone_number || "",
        lender_email: initialData.lender_email || "",
        lender_name: initialData.lender_name || "",
        lender_institution: initialData.lender_institution || "",
        lender_phone_number: initialData.lender_phone_number || "",
        lender_title: initialData.lender_title || "",
        document_title: initialData.document_title || "",
      };
    }
    return defaultFormData;
  });

  // Determine country code for template selection
  const countryCodeForSelection = useMemo(() => {
    if (allowEditCountryType) {
      // Use form data if editable
      return formData.ach_form_country_type;
    } else {
      // Use initial data if not editable
      return initialData?.ach_form_country_type || "USA";
    }
  }, [allowEditCountryType, formData.ach_form_country_type, initialData?.ach_form_country_type]);

  // Auto-select template based on matching criteria
  const autoSelectedTemplate = useMemo(() => {
    if (filteredTemplates.length === 0) return null;

    const countryCode = countryCodeForSelection;

    if (allowEditLienholder) {
      // Modal version: match by both variant and country
      const exactMatch = filteredTemplates.find((template: ListAchTemplateItem) => {
        const templateTitle = template.DocumentTemplate?.title || "";

        const countryMatches = countryCode
          ? templateTitle.toUpperCase().includes(countryCode)
          : true;

        return countryMatches;
      });

      if (exactMatch) return exactMatch;
    } else {
      // Task instance version: match by country only (original behavior)
      const countryMatch = filteredTemplates.find((template: ListAchTemplateItem) => {
        const templateTitle = template.DocumentTemplate?.title || "";
        const countryMatches = countryCode
          ? templateTitle.toUpperCase().includes(countryCode)
          : true;
        return countryMatches;
      });

      if (countryMatch) return countryMatch;
    }

    return filteredTemplates[0];
  }, [filteredTemplates, countryCodeForSelection, allowEditLienholder]);

  // Set selected template when auto-selection changes or templates load
  useEffect(() => {
    if (autoSelectedTemplate && !formData.template_id) {
      setFormData((prev) => ({
        ...prev,
        template_id: autoSelectedTemplate.DocumentTemplate?.id || "",
        ach_document_id: autoSelectedTemplate.id,
      }));
    }
  }, [autoSelectedTemplate, formData.template_id]);

  // Reset form when resetTrigger changes
  useEffect(() => {
    if (resetTrigger) {
      setFormData(initialData ? {
        ...defaultFormData,
        ...initialData,
        customer_phone_number: initialData.customer_phone_number || "",
        lender_email: initialData.lender_email || "",
        lender_name: initialData.lender_name || "",
        lender_institution: initialData.lender_institution || "",
        lender_phone_number: initialData.lender_phone_number || "",
        lender_title: initialData.lender_title || "",
        document_title: initialData.document_title || "",
      } : defaultFormData);
      setFieldErrors({});
    }
  }, [resetTrigger, initialData]);

  // Validate lender fields
  const validateLenderFields = (): boolean => {
    const errors: { lender_email?: string; lender_institution?: string } = {};

    const hasLienholder = allowEditLienholder ? formData.has_lienholder : (initialData?.has_lienholder ?? false);

    if (hasLienholder) {
      if (!formData.lender_email?.trim()) {
        errors.lender_email = "Lender email is required";
      } else {
        // Validate email format using Zod
        const emailSchema = z.string().email();
        const emailResult = emailSchema.safeParse(formData.lender_email);
        if (!emailResult.success) {
          errors.lender_email = "Please enter a valid email address";
        }
      }

      if (!formData.lender_institution?.trim()) {
        errors.lender_institution = "Lender institution is required";
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!formData.template_id || !formData.ach_document_id) {
      toast({
        title: "Error",
        description: "Template is required",
        variant: "destructive",
      });
      return;
    }

    if (!formData.customer_name || !formData.customer_email) {
      toast({
        title: "Error",
        description: "Customer name and email are required",
        variant: "destructive",
      });
      return;
    }

    // Validate lender fields if lienholder is required
    if (!validateLenderFields()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the lender information section",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const hasLienholder = allowEditLienholder ? formData.has_lienholder : (initialData?.has_lienholder ?? false);

      const payload = {
        ach_document_id: formData.ach_document_id,
        group_id: uuidV4(),
        template_id: formData.template_id,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone_number: formData.customer_phone_number || undefined,
        lender_email: hasLienholder ? (formData.lender_email || undefined) : undefined,
        lender_name: hasLienholder ? (formData.lender_name || undefined) : undefined,
        lender_institution: hasLienholder ? (formData.lender_institution || undefined) : undefined,
        lender_phone_number: hasLienholder ? (formData.lender_phone_number || undefined) : undefined,
        lender_title: hasLienholder ? (formData.lender_title || undefined) : undefined,
        document_title: formData.document_title?.trim() || undefined,
        is_test_send: formData.is_test_send,
        has_lienholder: hasLienholder,
        ...(taskInstanceId && { task_instance_id: taskInstanceId }),
      };

      const parseResult = CreateTemplateInstanceValidation.safeParse(payload);

      if (!parseResult.success) {
        console.error("Failed to validate form data", parseResult.error);
        toast({
          title: "Validation Error",
          description: parseResult.error.errors.map((e) => e.message).join(", "),
          variant: "destructive",
        });
        return;
      }

      const apiResult = await createAchTemplateInstance(parseResult.data);

      if (!apiResult.success) {
        throw apiResult;
      }

      toast({
        title: "ACH Form Sent",
        description: formData.is_test_send
          ? "Test ACH form instance created successfully"
          : "ACH form instance created successfully",
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["ach"] }),
        queryClient.invalidateQueries({ queryKey: ["ach", "review-instances"] }),
      ]);

      onSuccess?.();
    } catch (error) {
      console.error("Failed to create ACH template instance", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to create ACH template instance";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePreview = async () => {
    if (isPreviewLoading || !formData.template_id) {
      if (!formData.template_id) {
        toast({
          title: "Error",
          description: "Template ID not found. Unable to generate preview.",
          variant: "destructive",
        });
      }
      return;
    }

    if (!formData.customer_name || !formData.customer_email) {
      toast({
        title: "Error",
        description: "Customer name and email are required for preview",
        variant: "destructive",
      });
      return;
    }

    // Validate lender fields if lienholder is required
    if (!validateLenderFields()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the lender information section",
        variant: "destructive",
      });
      return;
    }

    setIsPreviewLoading(true);
    try {
      const previewPayload: any = {
        template_id: formData.ach_document_id,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone_number: formData.customer_phone_number || undefined,
        lender_email: formData.lender_email || undefined,
        lender_name: formData.lender_name || undefined,
        lender_institution: formData.lender_institution || undefined,
        lender_phone_number: formData.lender_phone_number || undefined,
        lender_title: formData.lender_title || undefined,
        has_lienholder: hasLienholder,
      };

      if (taskInstanceId) {
        previewPayload.task_instance_id = taskInstanceId;
      }

      const bodyValuesResult = PreviewBodyValidation.safeParse(previewPayload);

      if (!bodyValuesResult.success) {
        throw new Error("Required email and name");
      }

      const result = await getAchPreview({
        ...bodyValuesResult.data,
        template_id: formData.ach_document_id,
      });

      if (result instanceof Blob) {
        const url = URL.createObjectURL(result);
        window.open(url, '_blank');
        // Clean up the URL after a short delay
        setTimeout(() => URL.revokeObjectURL(url), 100);
      } else {
        toast({
          title: "Error",
          description: "Failed to generate preview",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Failed to generate preview", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to generate preview";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const selectedTemplate = filteredTemplates.find(
    (t: ListAchTemplateItem) => t.id === formData.ach_document_id
  );

  const hasLienholder = allowEditLienholder ? formData.has_lienholder : (initialData?.has_lienholder ?? false);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="template_id">
              ACH Template <span className="text-destructive">*</span>
            </Label>
            <div className="flex gap-2">
              <Select
                value={formData.ach_document_id}
                onValueChange={(value) => {
                  const template = filteredTemplates.find((t: ListAchTemplateItem) => t.id === value);
                  setFormData((prev) => ({
                    ...prev,
                    ach_document_id: value,
                    template_id: template?.DocumentTemplate?.id || "",
                  }));
                }}
                disabled={templatesResult.isLoading || filteredTemplates.length === 0}
              >
                <SelectTrigger id="template_id" className="flex-1">
                  <SelectValue
                    placeholder={
                      templatesResult.isLoading ? "Loading templates..." : "Select template..."
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {filteredTemplates.map((template: ListAchTemplateItem & { ach_variant?: string }) => {
                    const templateTitle = template.DocumentTemplate?.title || "Untitled";
                    return (
                      <SelectItem key={template.id} value={template.id}>
                        {templateTitle}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isPreviewLoading || !formData.template_id || templatesResult.isLoading}
                onClick={handlePreview}
                className="gap-1.5"
              >
                {isPreviewLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileText className="h-4 w-4" />
                )}
                Preview
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="document_title">Document Title (Optional)</Label>
            <Input
              id="document_title"
              value={formData.document_title}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, document_title: e.target.value }))
              }
              placeholder="Enter document title..."
            />
          </div>
        </div>

        {(allowEditLienholder || allowEditCountryType) && (
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-4">Form Options</h3>
            <div className="space-y-3">
              {allowEditLienholder && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="has_lienholder"
                    checked={formData.has_lienholder}
                    onCheckedChange={(checked) => {
                      setFormData((prev) => ({ ...prev, has_lienholder: checked === true }));
                      // Clear lender field errors when lienholder is unchecked
                      if (!checked) {
                        setFieldErrors({});
                      }
                    }}
                  />
                  <Label htmlFor="has_lienholder" className="cursor-pointer">
                    Lienholder required
                  </Label>
                </div>
              )}

              {allowEditCountryType && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="ach_form_country_type_cad"
                    checked={formData.ach_form_country_type === "CAD"}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({
                        ...prev,
                        ach_form_country_type: checked ? "CAD" : "USA",
                      }))
                    }
                  />
                  <Label htmlFor="ach_form_country_type_cad" className="cursor-pointer">
                    CAD (default: USA)
                  </Label>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="border-t pt-4">
          <h3 className="text-lg font-semibold mb-4">Customer Information</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="customer_name">
                Customer Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="customer_name"
                value={formData.customer_name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, customer_name: e.target.value }))
                }
                required
                placeholder="Enter customer name..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="customer_email">
                Customer Email <span className="text-destructive">*</span>
              </Label>
              <Input
                id="customer_email"
                type="email"
                value={formData.customer_email}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, customer_email: e.target.value }))
                }
                required
                placeholder="Enter customer email..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="customer_phone_number">Customer Phone Number</Label>
              <Input
                id="customer_phone_number"
                type="tel"
                value={formData.customer_phone_number || ""}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, customer_phone_number: e.target.value }))
                }
                placeholder="Enter customer phone number..."
              />
            </div>
          </div>
        </div>

        {hasLienholder && (
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-4">Lender Information</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="lender_email">
                  Lender Email <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="lender_email"
                  type="email"
                  value={formData.lender_email || ""}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, lender_email: e.target.value }));
                    // Clear error when user starts typing
                    if (fieldErrors.lender_email) {
                      setFieldErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.lender_email;
                        return newErrors;
                      });
                    }
                  }}
                  placeholder="Enter lender email..."
                  className={fieldErrors.lender_email ? "border-destructive" : ""}
                />
                {fieldErrors.lender_email && (
                  <p className="text-sm text-destructive">{fieldErrors.lender_email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="lender_name">
                  Lender Name
                </Label>
                <Input
                  id="lender_name"
                  value={formData.lender_name || ""}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, lender_name: e.target.value }));
                    // Clear error when user starts typing
                    if (fieldErrors.lender_name) {
                      setFieldErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.lender_name;
                        return newErrors;
                      });
                    }
                  }}
                  placeholder="Enter lender name..."
                  className={fieldErrors.lender_name ? "border-destructive" : ""}
                />
                {fieldErrors.lender_name && (
                  <p className="text-sm text-destructive">{fieldErrors.lender_name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="lender_institution">Lender Institution <span className="text-destructive">*</span></Label>
                <Input
                  id="lender_institution"
                  value={formData.lender_institution || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, lender_institution: e.target.value }))
                  }
                  placeholder="Enter lender institution..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lender_title">Lender Title</Label>
                <Input
                  id="lender_title"
                  value={formData.lender_title || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, lender_title: e.target.value }))
                  }
                  placeholder="Enter lender title..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lender_phone_number">Lender Phone Number</Label>
                <Input
                  id="lender_phone_number"
                  type="tel"
                  value={formData.lender_phone_number || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, lender_phone_number: e.target.value }))
                  }
                  placeholder="Enter lender phone number..."
                />
              </div>
            </div>
          </div>
        )}

        <div className="border-t pt-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="is_test_send"
              checked={formData.is_test_send}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, is_test_send: checked === true }))
              }
            />
            <Label htmlFor="is_test_send" className="cursor-pointer">
              Send as test (will not send to customer)
            </Label>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending...
            </>
          ) : (
            "Send ACH Form"
          )}
        </Button>
      </div>
    </form>
  );
}

